<template>
	<StandardModal
		open
		:title="title"
		:cancelText="$t('common_button_cancel')"
		:confirmText="confirmText"
		:confirmProps="{ disabled, loading: submitting }"
		@cancel="$emit('cancel')"
		@confirm="addOrUpdate"
		@body-scroll="closeRoleDropdown"
		ref="modal"
		class="add-person-modal"
	>
		<ProfilePic
			v-if="person"
			size="70px"
			:user="profileUser"
			class="profile-pic"
			alt="profile"
		/>

		<FormItem
			:label="$t('admin_people_first_name_label')"
			:error="!!errors.firstname"
			:message="errors.firstname"
		>
			<Input
				class="first-name"
				:placeholder="$t('admin_people_first_name_example_label')"
				:disabled="!canEditField('name')"
				v-model="firstname"
				autoFocus
			>
				<template #after v-if="!canEditField('name')">
					<LockKeyholeIcon />
				</template>
			</Input>
		</FormItem>

		<FormItem
			:label="$t('admin_people_last_name_label')"
			:error="!!errors.lastname"
			:message="errors.lastname"
		>
			<Input
				class="last-name"
				:placeholder="$t('admin_people_last_name_example_label')"
				:disabled="!canEditField('name')"
				v-model="lastname"
			>
				<template #after v-if="!canEditField('name')">
					<LockKeyholeIcon />
				</template>
			</Input>
		</FormItem>

		<FormItem
			v-if="pronounsEnabled"
			:label="$t('common_pronouns')"
			:error="!!errors.pronouns"
			:message="errors.pronouns"
		>
			<Input
				class="pronouns"
				:placeholder="$t('settings_profile_pronouns_placeholder')"
				:disabled="!canEditField('pronouns')"
				v-model="pronouns"
				maxlength="40"
			>
				<template #after v-if="!canEditField('pronouns')">
					<LockKeyholeIcon />
				</template>
			</Input>
		</FormItem>

		<FormItem
			v-if="titleEnabled"
			:label="$t('common_title')"
			:error="!!errors.title"
			:message="errors.title"
		>
			<Input
				class="user-title"
				:placeholder="$t('settings_profile_title_placeholder')"
				:disabled="!canEditField('title')"
				v-model="userTitle"
				maxlength="40"
			>
				<template #after v-if="!canEditField('title')">
					<LockKeyholeIcon />
				</template>
			</Input>
		</FormItem>

		<FormItem
			:label="$t('admin_people_email_label')"
			:error="!!errors.email"
			:message="errors.email"
		>
			<Input
				class="email"
				placeholder="name@domain.com"
				:disabled="!canEditField('email')"
				v-model="email"
			>
				<template #after v-if="!canEditField('email')">
					<LockKeyholeIcon />
				</template>
			</Input>
		</FormItem>

		<FormItem
			:label="$t('admin_people_phone_label')"
			:error="!!errors.phone"
			:message="errors.phone"
		>
			<PhoneInput
				v-model="phone"
				:disabled="!canEditField('phone')"
				:error="!!errors.phone"
				:placeholder="$t('admin_people_mobile_phone_placeholder')"
				ref="phone"
			>
				<template #after v-if="!canEditField('phone')">
					<LockKeyholeIcon />
				</template>
			</PhoneInput>
		</FormItem>

		<FormItem
			:label="$t('admin_people_organization_role_label')"
			:error="!!errors.role"
			:message="errors.role"
			class="organization-role"
		>
			<Dropdown
				class="org-role"
				:options="options"
				v-model="role"
				:global="!notGlobal"
				:disabled="!canEditField('role')"
				ref="roleDropdown"
				optionsClass="user-role"
			>
				<template #selected="{ option }">
					{{ $t(option.label) }}
				</template>

				<template v-slot="{ option }">
					{{ $t(option.label) }}
				</template>

				<template #right-content v-if="!canEditField('role')">
					<LockKeyholeIcon />
				</template>
			</Dropdown>
		</FormItem>

		<Checkbox v-if="!person" v-model="welcomeChecked">
			{{ $t('settings_edituser_welcomemessage') }}
		</Checkbox>

		<template #footer-extra v-if="locked">
			<ActionList localize :items="lockedRecordItem" />
		</template>
	</StandardModal>
</template>

<script>
import {
	StandardModal,
	FormItem,
	Input,
	Dropdown,
	ProfilePic,
	PhoneInput,
	Checkbox,
} from '@/components/common'
import { post, patch, ActionList } from '../common'
import { isValidEmail, storage } from '@/helpers/utils'
import { LockKeyholeIcon } from '@/icons'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { USER_TITLE, USER_PRONOUNS, orgFlag } from '@/helpers/flags.js'

const options = [
	{ id: 'user', label: 'model_role_user' },
	{ id: 'manager', label: 'model_role_manager' },
	{ id: 'admin', label: 'model_role_admin' },
]

export default {
	name: 'AddPerson',

	props: {
		person: null,
		notGlobal: { type: Boolean, default: null }, // Hack: simply used as an override in tests
	},
	components: {
		StandardModal,
		FormItem,
		Input,
		Dropdown,
		ProfilePic,
		ActionList,
		LockKeyholeIcon,
		PhoneInput,
		Checkbox,
	},

	data() {
		let countryCode = 'US'
		let {
			firstname = '',
			lastname = '',
			email = '',
			phone = '',
			title = '',
			pronouns = '',
			role,
			locked,
		} = this.person || {}
		if (phone) {
			const phoneInfo = parsePhoneNumberFromString(phone)
			countryCode = phoneInfo?.country || null
			phone = phoneInfo?.number || null
		}
		return {
			errorClose: null,
			firstname,
			lastname,
			userTitle: title,
			pronouns,
			email,
			phone,
			locked,
			countryCode,
			errors: {},
			submitting: false,
			options,
			role: role || options[0].id,
			welcomeChecked: true,
		}
	},

	emits: ['cancel', 'success'],

	computed: {
		confirmText() {
			return this.person
				? this.$t('common_button_save')
				: this.$t('common_add_button_title')
		},

		title() {
			return this.person
				? this.$t('settings_edituser_title')
				: this.$t('people_add_selection_button_title')
		},

		hasEditChanged() {
			if (!this.person) return false
			return (
				this.person.firstname !== this.firstname ||
				this.person.lastname !== this.lastname ||
				this.person.email !== this.email ||
				this.person.phone !== this.phone ||
				this.person.role !== this.role ||
				this.person.title !== this.userTitle ||
				this.person.pronouns !== this.pronouns
			)
		},

		disabled() {
			// if nothing has changed, save should be disabled
			if (this.person && !this.hasEditChanged) return true
			// firstname and lastname are always required
			if (!this.firstname || !this.lastname) return true
			// if creating new users, phone OR email is required
			if (!this.person && !this.email && !this.phone) return true
			// if existing user is active, phone OR email is required
			if (this.person?.active && !this.email && !this.phone) return true
			// otherwise, any fields can be edited
			return false
		},

		profileUser() {
			return { ...this.person, profilepic: this.person.profilepicurl }
		},

		lockedRecordItem() {
			return [
				{
					id: 'locked',
					icon: LockKeyholeIcon,
					type: 'primary',
					label: 'admin_common_locked_record_label',
					noClick: true,
				},
			]
		},

		titleEnabled() {
			return orgFlag(USER_TITLE)
		},

		pronounsEnabled() {
			return orgFlag(USER_PRONOUNS)
		},
	},

	methods: {
		addOrUpdate() {
			this.person ? this.updateUser() : this.addUser()
		},

		closeRoleDropdown() {
			this.$refs.roleDropdown.close()
		},

		displayErrors(e) {
			console.error(e)
			if (!e.response?.data?.errors) {
				this.modalError = this.$refs.modal.$alert.error()
				return
			}
			this.errors = Object.entries(e.response?.data?.errors).reduce(
				(acc, [key, errors]) => {
					acc[key] = errors[0].message
					return acc
				},
				{}
			)
		},

		validateAndGetPayload() {
			this.modalError?.()
			this.errors = {}
			if (!this.$refs.phone.isValid()) {
				this.errors.phone = this.$t('apierrors_INVALID_PHONE_PHONE')
			}
			if (this.email && !isValidEmail(this.email)) {
				this.errors.email = this.$t('apierrors_INVALID_EMAIL_EMAIL')
			}
			if (Object.keys(this.errors).length) {
				return null
			}

			const payload = {}

			if (this.canEditField('name')) {
				;(payload.firstname = this.firstname),
					(payload.lastname = this.lastname)
			}

			if (this.canEditField('phone')) {
				payload.phone = this.phone || null
			}

			if (this.canEditField('email')) {
				payload.email = this.email || null
			}

			if (this.canEditField('role')) {
				payload.role = this.role
			}

			if (this.titleEnabled) {
				payload.title = this.userTitle
			}

			if (this.pronounsEnabled) {
				payload.pronouns = this.pronouns
			}

			if (!this.person) {
				payload.send_welcome_message = this.welcomeChecked
			}
			return payload
		},

		async addUser() {
			try {
				const payload = this.validateAndGetPayload()
				if (!payload) return
				this.submitting = true
				const user = await post('/organization/users', payload)
				storage.set('org_mgmt_changed_people', true)
				this.$emit('success', user)
				this.$alert.success(this.$t('admin_people_person_added_label'))
			} catch (e) {
				this.displayErrors(e)
			}
			this.submitting = false
		},

		async updateUser() {
			try {
				const payload = this.validateAndGetPayload()
				if (!payload) return
				this.submitting = true
				const user = await patch(
					`/organization/users/${this.person.id}`,
					payload
				)
				storage.set('org_mgmt_changed_people', true)
				this.$emit('success', user)
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
			} catch (e) {
				this.displayErrors(e)
			}
			this.submitting = false
		},

		canEditField(field) {
			if (!this.person?.permissions) return true
			return this.person.permissions[`change_${field}`] !== 'system'
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.profile-pic {
	margin-bottom: 16px;
	cursor: pointer;
}

.organization-role {
	max-width: 200px;
}
</style>

<style lang="scss">
.dropdown-options-container.user-role {
	width: 200px !important;
}

.add-person-modal {
	.pronto-input {
		.after {
			.pronto-icon {
				color: var(--neutral-400);
				margin-right: 12px;
			}
		}
	}
	.pronto-phone {
		.after {
			.pronto-icon {
				color: var(--neutral-400);
				margin-right: 12px;
			}
		}
	}

	.modal-body {
		padding: 24px;
	}

	.form-item {
		margin-bottom: 16px;
	}

	.input-content {
		background-color: #fafafa;
	}
}
</style>
